import styled, { css, keyframes } from 'styled-components';
// Leaf styled module (styled-components only, no barrel), so extending the Menu
// AI glare here carries no import-cycle risk and keeps one copy of the sweep.
import { ItemGlare } from '../../Menu/MenuAIImprove/menuAiGlow.styled';

/**
 * One message: the row it sits in, the shapes it can take (a bubble, a captioned
 * media card, or neither) and the hover actions beside it.
 */

/**
 * `$grouped` marks a message that continues the previous one's run. Runs stack
 * tight; a new run gets the full gap, which is what makes the grouping legible
 * without any separator.
 */
export const MessageRow = styled.div<{ $own: boolean; $grouped?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  justify-content: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
  margin-top: ${({ $grouped }) => ($grouped ? '0' : '12px')};
  /* Anchor for the swipe-to-reply disc. */
  position: relative;
  /*
   * The row is swiped horizontally to reply. pan-y hands the vertical axis to
   * the browser - the thread still scrolls natively over the messages - while a
   * horizontal drag keeps firing pointer moves instead of being claimed as a
   * scroll and cancelled mid-gesture.
   */
  touch-action: pan-y;
`;

/**
 * The reply disc a swipe reveals, hanging off the bubble's leading edge.
 *
 * Inside the element the gesture translates, on purpose: it RIDES the bubble
 * rather than waiting at a fixed edge - WhatsApp's arrangement, which is what
 * the gesture here is modelled on. An incoming bubble starts at the screen's
 * left edge, so the disc begins off screen and slides into view with the pull.
 *
 * Painted by the gesture, not by React: `useSwipeToReply` drives opacity and
 * scale per pointer move, so the resting state here is invisible-and-small and
 * everything else is inline style.
 *
 * Touch screens only - a mouse cannot make this gesture, and the disc would
 * otherwise flash on the desktop's own drags (text selection over a message).
 */
export const SwipeReplyHint = styled.span`
  display: none;
  position: absolute;
  right: 100%;
  margin-right: 8px;
  top: 50%;
  margin-top: -16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  opacity: 0;
  transform: scale(0.6);
  pointer-events: none;

  svg {
    width: 14px;
    height: 14px;
    display: block;
  }

  @media (hover: none) {
    display: flex;
  }
`;

export const BubbleColumn = styled.div<{ $own: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
  max-width: 70%;
  min-width: 0;

  @media (max-width: 800px) {
    max-width: 100%;
  }
`;

/** The bubble's own line, inside the column that also holds the name under it. */
export const BubbleLine = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  /* Anchor for the swipe-to-reply disc, which hangs off the bubble - and off
     THIS element rather than the row, so it centres on the bubble alone and
     not on the bubble plus the name under it. */
  position: relative;
`;

/**
 * Anchor for the hover actions: wraps the bubble ONLY, so the actions sit
 * against the bubble's real edge. Anchoring to the column instead would push
 * them out to wherever the meta line ends, which is frequently wider than a
 * short message.
 */
export const BubbleShell = styled.div<{ $own?: boolean }>`
  position: relative;
  /* A column, because one message can be several shapes: a bubble, a media
     group and a voice pill can all belong to the same message and have to
     stack rather than sit in a row. */
  display: flex;
  flex-direction: column;
  align-items: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
  gap: 4px;
  min-width: 0;
  /* Only the jump-target glare uses this: it inherits the radius to clip its
     sweep, and the shell is the one wrapper every shape shares. */
  border-radius: 16px;

  /*
   * On a touch screen this is press-and-hold territory - the action sheet is
   * how a phone gets at reply, edit and delete. Left alone, holding a message
   * raises iOS's own text-selection callout over the sheet and starts a
   * selection drag on Android.
   *
   * Scoped to devices with no hover: a mouse keeps the hover gutter AND keeps
   * being able to select a message to copy it.
   */
  @media (hover: none) {
    -webkit-touch-callout: none;
    user-select: none;
  }
`;

/**
 * The one-shot sweep played over a message a reply jumped back to.
 *
 * Two things the shared `ItemGlare` cannot know on its own:
 *
 * The stacking. `Bubble` is `position: relative` (it anchors its own meta), so
 * the glare and the shape it lays over are BOTH positioned with `z-index: auto`
 * - and among those, document order decides who paints on top. The glare is
 * rendered first, so without a z-index the bubble covered it completely and the
 * sweep never showed at all.
 *
 * The colour. The shared one sweeps in `primary`, which was written against the
 * menu's white rows. Half the messages here are a solid `primary` bubble, and
 * red on red is nothing at all - those get white instead.
 */
/**
 * The same band, crossing again and again for as long as Viktor is working.
 *
 * The pause lives in the keyframes rather than in a delay: the band reaches
 * the far side at 66% and waits out the rest of the cycle there. A sweep with
 * no rest in it reads as a progress bar - something measurable, filling up -
 * and this measures nothing; it only says he is still at it.
 *
 * The px on top of the percentages are what make the rest invisible, and they
 * are not padding. The band is skewed 20 degrees, so its bottom edge sits
 * `tan(20) * height / 2` to the LEFT of its top edge - on a bubble as tall as
 * it is wide that is nothing, on one of Viktor's long answers (measured: 2036
 * tall in a 718 wide bubble) it is 370px. The shared -160%/260%, which is a
 * share of the band's own width and knows nothing about height, left that
 * bottom corner 155px INSIDE the bubble: the band parked in the lower right
 * for the whole rest and then vanished as the next pass began. 420px clears
 * a bubble nearly five times taller than it is wide, which the 70% width cap
 * puts well out of reach.
 *
 * The duration grew with the distance so the band still crosses at the speed
 * it did - the extra travel is all off-screen, so only the gap between passes
 * is longer.
 */
const streamSweep = keyframes`
  0% { transform: translateX(calc(-160% - 420px)) skewX(-20deg); }
  66%, 100% { transform: translateX(calc(260% + 420px)) skewX(-20deg); }
`;

export const BubbleGlare = styled(ItemGlare)<{
  $own?: boolean;
  /**
   * Sweep on a loop instead of once. For Viktor's answer while it is still
   * being written - and above all for the long quiet stretches inside it,
   * where he is checking data and not a character moves for twenty seconds.
   */
  $streaming?: boolean;
}>`
  z-index: 1;

  ::after {
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme, $own }) =>
        $own ? `${theme.white}A6` : `${theme.primary}66`},
      transparent
    );

    ${({ $streaming }) =>
      $streaming &&
      css`
        /* Slower than the one-shot, which is a flick of attention at the end
           of a jump. This one has to be bearable for minutes. */
        animation: ${streamSweep} 3s ease-in-out infinite;
      `}
  }

  /*
   * An animation with no end is exactly what this setting is asking about.
   * The band parks off the left edge and the bubble simply sits quiet; the
   * composer still shows Stop, so nothing about the state is lost.
   */
  @media (prefers-reduced-motion: reduce) {
    ::after {
      animation: none;
    }
  }
`;

/**
 * Reply / edit / delete.
 *
 * Absolutely positioned in the gutter beside the bubble, so revealing them
 * never reflows the thread - the previous version rendered them in flow below
 * the message, which pushed everything underneath it down on every hover.
 *
 * Driven by CSS `:hover` on the row rather than React state: no re-render per
 * mouse move, and the fade is free. Hidden below 800px, where there is no
 * hover and no room in the gutter.
 */
export const HoverActions = styled.div<{ $own: boolean }>`
  position: absolute;
  /*
   * In the gutter beside the bubble, centred on it rather than pinned to its
   * top: against a tall bubble (an image, or the audio pill) a top-aligned
   * button reads as belonging to the message above it. BubbleShell wraps the
   * bubble alone, so 50% here is the bubble's own middle.
   *
   * Every offset reads a variable so a surface too narrow for that gutter can
   * move the pill without this file knowing which surface it is. The defaults
   * ARE the gutter placement, so a thread that publishes nothing gets exactly
   * what it always got; the 400px chat bubble panel republishes them (see
   * ChatBubble.styled), where the gutter is thinner than the pill and it hung
   * off the panel's edge.
   */
  top: var(--chat-actions-top, 50%);
  transform: translateY(var(--chat-actions-shift, -50%));
  /* One offset rather than an edge plus a margin: an absolute box is placed by
     its margin box, so 100% + 8px is the same 8px of air, in a single value a
     panel can override. */
  ${({ $own }) =>
    $own
      ? 'right: var(--chat-actions-inset, calc(100% + 8px));'
      : 'left: var(--chat-actions-inset, calc(100% + 8px));'}

  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 20px;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};

  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  ${MessageRow}:hover &, &:focus-within {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  /*
   * Bridges the air under the pill when it is placed ABOVE the bubble.
   *
   * The pill only exists while the row is hovered, and it is the row's own box
   * that has to stay under the pointer for that: the default gutter is inside
   * that box, so crossing the 8px to the pill never breaks the hover, but air
   * above the bubble is outside it - the pointer left the row, the pill went
   * pointer-events: none mid-travel, and the buttons could not be reached.
   * This invisible strip reaches from the pill down onto the bubble, so the
   * pointer is over a child of the row the whole way. Zero by default: nothing
   * to bridge where the pill sits in the gutter.
   */
  ::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: var(--chat-actions-bridge, 0px);
  }

  @media (max-width: 800px) {
    display: none;
  }
`;

export const MessageActionButton = styled.button<{ $danger?: boolean }>`
  width: 28px;
  height: 28px;
  flex: none;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  /* The shared icons ship at their own natural sizes (the delete glyph is
     enormous), so pin them here instead of trusting each one. */
  svg {
    width: 14px;
    height: 14px;
    display: block;
  }

  svg path {
    fill: ${({ theme, $danger }) => ($danger ? theme.primary : theme.darkGray)};
    transition: ${({ theme }) => theme.transitions.ease200ms};
  }

  :hover {
    background: ${({ theme, $danger }) =>
      $danger ? theme.transparentRed : theme.gray};
  }
`;

/**
 * Timestamp (plus "Edited" and the seen ticks) sitting inside the bubble, the
 * way Telegram does it.
 *
 * `float: right` after the body is what puts it on the last line when there is
 * room and drops it to its own line when there is not - no measuring, no
 * reserved gutter on every line. It follows the content in source order for
 * exactly that reason: floated first, the text would wrap around it from the
 * very first line.
 */
export const BubbleMeta = styled.span<{ $own: boolean }>`
  float: right;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0 12px;
  font-size: 11px;
  line-height: 14px;
  white-space: nowrap;
  /* Dimmed white on the red bubble, grey on the white one - each is the quiet
     version of its own surface's text colour. */
  color: ${({ theme, $own }) => ($own ? `${theme.white}B3` : theme.lightGray)};

  /* One step up with the body it shares a line with, so the time does not end
     up noticeably smaller than it is on a desktop. */
  @media (max-width: 800px) {
    font-size: 12px;
    line-height: 16px;
  }

  svg {
    display: block;
  }
`;

/**
 * A name inside a message.
 *
 * Weight alone, not colour, is what carries it on your own bubble: that one is
 * solid red with white text, and every colour available reads as damage there.
 * On the white side the brand red is free to do the work.
 *
 * $me is the handle that names the reader. It gets a chip rather than more
 * weight, because the point is not that a name is written here - it is that the
 * message is addressed to you, and that has to be findable while scrolling past
 * without reading a word.
 */
export const Mention = styled.span<{
  $own?: boolean;
  $me?: boolean;
  $clickable?: boolean;
}>`
  font-weight: 500;
  color: ${({ theme, $own }) => ($own ? theme.white : theme.primary)};
  /* Offset so the line clears the descenders in a name rather than cutting
     through them. */
  text-decoration: underline;
  text-underline-offset: 2px;

  ${({ $me, $own, theme }) =>
    $me &&
    !$own &&
    `
    background: ${theme.transparentRed};
    border-radius: 4px;
    padding: 0 3px;
    margin: 0 -3px;
  `}

  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
`;

/**
 * A URL in a message, wearing the mention's colours - on each surface there is
 * exactly one ink left over for "this is interactive", and two different
 * accents inside one bubble would read as two kinds of importance rather than
 * two kinds of link.
 *
 * `overflow-wrap: anywhere` is half the point: a pasted spreadsheet URL is one
 * unbroken word several lines long, and without this it either overflows the
 * bubble or forces it to the full 70% cap. The bubble's own `break-word` does
 * not apply to the anchor's content in every engine, so it is stated here.
 */
export const BodyLink = styled.a<{ $own?: boolean }>`
  /* Doubled class: the global stylesheet colours a:link, and the pseudo-class
     puts that rule above a single class. This outranks it without an
     !important. */
  && {
    font-weight: 500;
    color: ${({ theme, $own }) => ($own ? theme.white : theme.primary)};
    text-decoration: underline;
    text-underline-offset: 2px;
    overflow-wrap: anywhere;
    word-break: break-all;
    cursor: pointer;
  }

  &&:hover {
    text-decoration: none;
  }
`;

/**
 * Whether a bubble sits on a coloured fill: your own, on the brand red, and
 * Viktor's, on the AI gradient. Kept as one function so background, ink, edge
 * and selection can never disagree about which kind of surface a bubble is.
 */
const onColouredFill = ({
  $own,
  $viktor,
}: {
  $own: boolean;
  $viktor?: boolean;
}) => $own || !!$viktor;

/**
 * The glow around the bubble Viktor is writing into.
 *
 * Three shadows on the bubble itself, in the two ends of the AI ramp and the
 * purple between them, so the light coming off it is the bubble's own colours:
 * blue leaning left, magenta leaning right.
 *
 * It used to be what the Menu's improve panel does - an absolutely positioned
 * span carrying the flowing gradient under `filter: blur()` - and that is the
 * thing that broke on a phone. A blurred element with a running animation is
 * composited into a layer of its own, and iOS does not reliably invalidate
 * that layer when the element RESIZES under it: the bubble grows from
 * "Reading your question..." into a whole answer, and the old layer is left
 * standing beside the new one as a pale rectangle. The menu card never showed
 * it because a card does not change size while it glows.
 *
 * A shadow is drawn WITH the shape rather than as a layer over it, so it
 * cannot outlive the shape that cast it. What is lost is the travel - the
 * gradient no longer drifts through the glow - and that is the price of being
 * right on the device the chat is mostly read on. The sweep across the words
 * is what keeps the bubble alive.
 */
const workingGlow = css`
  /* The ramp's own stops: 306FDE at one end, BA39B5 at the other, 7F50C7
     between them. Written out because the theme exposes the ramp only as a
     finished gradient, and a shadow needs the colours themselves. */
  box-shadow: -6px 0 22px 2px rgba(48, 111, 222, 0.5),
    6px 0 22px 2px rgba(186, 57, 181, 0.45), 0 0 16px rgba(127, 80, 199, 0.45);
`;

export const Bubble = styled.div<{
  $own: boolean;
  /** One of Viktor's answers - the gradient side of the pairing. */
  $viktor?: boolean;
  /** A dispatcher note, which the customer does not see - see `messageTone`. */
  $tone?: 'note' | null;
  /** Viktor is still writing into it - see `workingGlow`. */
  $working?: boolean;
  $deleted?: boolean;
}>`
  /* Contains the floated BubbleMeta, which would otherwise escape the bubble
     whenever it lands on a line of its own. */
  ::after {
    content: '';
    display: block;
    clear: both;
  }

  /* Anchors the glare overlay played when a reply jumps back to this message. */
  position: relative;
  /* The 70% cap lives on BubbleColumn; capping again here would compound. */
  max-width: 100%;
  padding: 7px 12px;
  border-radius: 16px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 20px;

  /*
   * Your own messages carry the brand red; what comes back is a plain white
   * card. The pairing is the point - one side strong, one side quiet - so a
   * thread can be read at a glance without checking which edge a bubble is on.
   * Viktor is the one exception on the incoming side: his answers wear the AI
   * gradient every AI surface in this app announces itself with, which is
   * what tells an answer from a colleague's reply at a glance. Two coloured
   * sides, but two different hues, and the alignment still says whose is whose.
   *
   * The border and shadow follow the WHITE side, not the coloured one: white on
   * the page's cream needs an edge to sit on it, while red - or the gradient -
   * is already separated from everything around it by hue alone.
   */
  background: ${({ theme, $own, $viktor }) =>
    $viktor ? theme.aiGradient : $own ? theme.primary : theme.white};
  color: ${({ theme, $own, $viktor }) =>
    onColouredFill({ $own, $viktor }) ? theme.white : theme.darkGray};
  border: ${({ theme, $own, $viktor }) =>
    onColouredFill({ $own, $viktor }) ? 'none' : theme.borders.border1};
  box-shadow: ${({ theme, $own, $viktor }) =>
    onColouredFill({ $own, $viktor }) ? 'none' : theme.shadows.shadow1};
  /* After the line above, which it replaces while the answer is arriving. */
  ${({ $working }) => $working && workingGlow}

  /* Over the fill above: the colour IS the audience, so it wins. */
  ${({ $tone, theme }) =>
    $tone === 'note' &&
    `
    background: ${theme.white};
    border: 1px dashed ${theme.chipYellow};
    box-shadow: none;
  `}

  /*
   * Selection on the coloured bubble. The browser default is a dark blue band,
   * which on solid primary red - or the gradient - reads as nothing at all:
   * selected text looked exactly like unselected text. Translucent white is
   * the one thing guaranteed to show on these surfaces, and the text stays
   * white under it. The descendant selector carries it into mentions, links
   * and the floated meta, which are their own elements and would otherwise
   * keep the default.
   */
  ${({ $own, $viktor, theme }) =>
    onColouredFill({ $own, $viktor }) &&
    `
    ::selection, *::selection {
      background: ${theme.white}59;
      color: ${theme.white};
    }
  `}

  ${({ $deleted, theme }) =>
    $deleted &&
    `
    font-style: italic;
    color: ${theme.lightGray};
    background: ${theme.gray};
    border: ${theme.borders.border1};
    box-shadow: none;
  `}

  /* Bigger on a phone: 14px is a comfortable reading size on a monitor an arm's
     length away and a small one held in the hand, where the bubble is also the
     only thing on screen and has the room to spare. */
  @media (max-width: 800px) {
    max-width: 100%;
    font-size: 16px;
    line-height: 22px;
  }
`;

/** The sender's name, once per run, under the last message of it. */
export const MessageMeta = styled.div<{ $own: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};
`;

/**
 * The name itself, which is a link to the person wherever the app can show one.
 *
 * Underline on hover rather than always: a run's label is repeated down the
 * whole thread, and a column of permanently underlined names reads as a list of
 * links instead of as who is speaking.
 */
export const SenderName = styled.span<{ $clickable?: boolean }>`
  ${({ $clickable }) =>
    $clickable &&
    `
    cursor: pointer;
    :hover { text-decoration: underline; }
  `}
`;

/**
 * A captioned message: media flush at the top, text in a strip below, the two
 * sharing one rounded surface. Unframed (no caption) it is a bare stack and
 * carries no background at all.
 */
export const MediaGroup = styled.div<{ $own: boolean; $framed?: boolean }>`
  display: flex;
  flex-direction: column;
  /* Unframed, each picture hugs its own side of the thread: two attachments of
     different widths would otherwise be stretched to the widest and left
     floating mid-column, belonging to neither side. Framed, they do stretch -
     the card is one surface and a ragged edge inside it reads as a mistake. */
  align-items: ${({ $framed, $own }) =>
    $framed ? 'stretch' : $own ? 'flex-end' : 'flex-start'};
  gap: ${({ $framed }) => ($framed ? '2px' : '4px')};
  min-width: 0;
  max-width: 100%;
  /* Matches MediaFrame so the clip and the child radius agree. */
  border-radius: 12px;
  /*
   * Only the framed card clips. Unframed there is nothing to clip - each
   * picture rounds itself - and clipping there cut the pictures' shadows off
   * flat along the bottom edge instead of letting them fall behind the name
   * or the next message.
   */
  overflow: ${({ $framed }) => ($framed ? 'hidden' : 'visible')};

  ${({ $framed, $own, theme }) =>
    $framed &&
    `
    border-radius: 16px;
    min-width: min(240px, 100%);
    background: ${$own ? theme.primary : theme.white};
    color: ${$own ? theme.white : theme.darkGray};
    border: ${$own ? 'none' : theme.borders.border1};
    box-shadow: ${$own ? 'none' : theme.shadows.shadow1};
  `}

  /* The caption strip sits on the same red as the bubble - same invisible
     default selection, same fix. See Bubble. */
  ${({ $framed, $own, theme }) =>
    $framed &&
    $own &&
    `
    ::selection, *::selection {
      background: ${theme.white}59;
      color: ${theme.white};
    }
  `}
`;

/**
 * The picture sets the width of a captioned message, and the text wraps inside
 * it - the group is shrink-to-fit, so anything that contributes its own
 * max-content width would stretch the whole card out to the 70% cap instead.
 *
 * Hence width 0 (contributes nothing) plus min-width 100% (fills whatever the
 * media settled on). The framed group's own min-width keeps a long caption
 * under a narrow photo from wrapping to a sliver.
 */
const spansTheMedia = css`
  width: 0;
  min-width: 100%;
`;

/** Reply quote / order link, above the media inside a framed group. */
export const MediaHeader = styled.div`
  ${spansTheMedia}
  padding: 6px 6px 0;
`;

/** The caption strip - the part of the picture that extends down for text. */
export const MediaCaption = styled.div`
  ${spansTheMedia}
  padding: 6px 12px 7px;
  font-size: 14px;
  line-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;

  /* Follows the bubble, or a caption would be the one line on the screen that
     stayed small. */
  @media (max-width: 800px) {
    font-size: 16px;
    line-height: 22px;
  }

  /* Contains the floated BubbleMeta, exactly as the bubble does. */
  ::after {
    content: '';
    display: block;
    clear: both;
  }
`;

/**
 * Timestamp for a message that is nothing but media, so there is no text line
 * to carry it. Laid over the picture on a scrim, which is the only place it can
 * go without adding a row of chrome under every photo.
 */
export const MediaTimeChip = styled.span<{ $topLeft?: boolean }>`
  position: absolute;
  z-index: 1;
  /* Videos take the top-left corner: the bottom edge is where the scrubber
     appears once playback starts, and the other two corners hold fullscreen
     and mute. */
  ${({ $topLeft }) =>
    $topLeft ? 'top: 8px; left: 8px;' : 'right: 8px; bottom: 8px;'}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.45);
  color: ${({ theme }) => theme.white};
  font-size: 11px;
  line-height: 14px;
  white-space: nowrap;
  pointer-events: none;

  svg {
    display: block;
  }
`;
